/**
 * BacktrackrContext.tsx - BacktrackrProvider - Context provider for versioning system
 * 
 * CHANGE HISTORY:
 * - 251021_0543 [d4517d3] danielbeer: feat: apply performance optimizations to all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import HistoryManager from './HistoryManager';
// REMOVED: import { useActr } from '@opcentral/actr'; - causes circular dependency
// Actr is optional and passed as prop

export interface BacktrackrContextValue {
  // State
  canUndo: boolean;
  canRedo: boolean;
  currentContext: string | null;
  activeContextHistory: any;
  stats: any;
  focusedField: any;
  
  // Recording
  recordChange: (change: any) => number;
  recordFieldChange: (nodeId: string, fieldName: string, before: any, after: any, metadata?: any) => number;
  recordStructureChange: (type: string, description: string, before: any, after: any, metadata?: any) => number;
  
  // Focus management
  setFocusedField: (nodeId: string | null, fieldName: string | null) => void;
  clearFocusedField: () => void;
  
  // Undo/Redo
  undo: () => Promise<any>;
  redo: () => Promise<any>;
  undoInContext: (contextId: string) => Promise<any>;
  redoInContext: (contextId: string) => Promise<any>;
  
  // Navigation
  goToChange: (changeId: number) => Promise<any>;
  
  // Branching
  createBranch: (name: string, description?: string) => any;
  switchBranch: (branchId: string) => Promise<any>;
  getBranches: () => any[];
  
  // History access
  getHistoryTree: () => any;
  getLinearHistory: () => any[];
  getContextHistory: (contextId: string) => any;
  
  // Import/Export
  exportHistory: () => any;
  importHistory: (data: any) => void;
  
  // Direct access
  historyManager: HistoryManager;
}

export const BacktrackrContext = createContext<BacktrackrContextValue | null>(null);

/**
 * BacktrackrProvider - Context provider for versioning system
 * Provides context-aware undo/redo functionality
 */
export const BacktrackrProvider = ({ children, options = {}, actr = null }) => {
  // console.log('[Backtrackr] BacktrackrProvider rendering'); // Disabled - too noisy
  
  // Actr is now passed as a prop instead of using useActr hook
  // This avoids circular dependency with Dependr
  const [historyManager] = useState(() => new HistoryManager({ ...options, actr }));
  const [state, setState] = useState({
    canUndo: false,
    canRedo: false,
    currentContext: null,
    activeContextHistory: null,
    stats: historyManager.getStats()
  });

  const focusedFieldRef = useRef(null);
  const contextTimeoutRef = useRef(null);

  // Update state when history changes
  useEffect(() => {
    const unsubscribe = historyManager.addListener((event, data) => {
      updateState();
    });

    return unsubscribe;
  }, [historyManager]);

  // Update state helper
  const updateState = useCallback(() => {
    const stats = historyManager.getStats();
    const contextHistory = state.currentContext 
      ? historyManager.getContextHistory(state.currentContext)
      : null;

    setState({
      canUndo: stats.canUndo || (contextHistory?.canUndo ?? false),
      canRedo: stats.canRedo || (contextHistory?.canRedo ?? false),
      currentContext: state.currentContext,
      activeContextHistory: contextHistory,
      stats
    });
  }, [historyManager, state.currentContext]);

  /**
   * Record a change
   */
  const recordChange = useCallback((change) => {
    return historyManager.recordChange(change);
  }, [historyManager]);

  /**
   * Record field change (context-specific)
   */
  const recordFieldChange = useCallback((nodeId, fieldName, before, after, metadata = {}) => {
    const contextId = `${nodeId}.${fieldName}`;
    
    return historyManager.recordChange({
      type: 'field',
      description: `Changed ${fieldName}`,
      context: contextId,
      before,
      after,
      metadata: {
        ...metadata,
        nodeId,
        fieldName
      }
    });
  }, [historyManager]);

  /**
   * Record structure change (global)
   */
  const recordStructureChange = useCallback((type, description, before, after, metadata = {}) => {
    return historyManager.recordChange({
      type: `structure.${type}`,
      description,
      context: null,
      before,
      after,
      metadata
    });
  }, [historyManager]);

  /**
   * Set focused field (for context-aware undo/redo)
   */
  const setFocusedField = useCallback((nodeId, fieldName) => {
    if (contextTimeoutRef.current) {
      clearTimeout(contextTimeoutRef.current);
    }

    const contextId = nodeId && fieldName ? `${nodeId}.${fieldName}` : null;
    
    // Delay context change to avoid rapid switching
    contextTimeoutRef.current = setTimeout(() => {
      historyManager.setActiveContext(contextId);
      setState(prev => ({
        ...prev,
        currentContext: contextId,
        activeContextHistory: contextId ? historyManager.getContextHistory(contextId) : null
      }));
    }, 100);

    focusedFieldRef.current = { nodeId, fieldName };
  }, [historyManager]);

  /**
   * Clear focused field
   */
  const clearFocusedField = useCallback(() => {
    if (contextTimeoutRef.current) {
      clearTimeout(contextTimeoutRef.current);
    }

    contextTimeoutRef.current = setTimeout(() => {
      historyManager.clearActiveContext();
      setState(prev => ({
        ...prev,
        currentContext: null,
        activeContextHistory: null
      }));
      focusedFieldRef.current = null;
    }, 100);
  }, [historyManager]);

  /**
   * Undo (context-aware)
   */
  const undo = useCallback(async () => {
    const result = await historyManager.undo();
    updateState();
    return result;
  }, [historyManager, updateState]);

  /**
   * Redo (context-aware)
   */
  const redo = useCallback(async () => {
    console.log('[Backtrackr] Redo function called');
    const result = await historyManager.redo();
    console.log('[Backtrackr] Redo result:', result);
    updateState();
    return result;
  }, [historyManager, updateState]);

  /**
   * Undo in specific context
   */
  const undoInContext = useCallback(async (contextId) => {
    const result = await historyManager.undoInContext(contextId);
    updateState();
    return result;
  }, [historyManager, updateState]);

  /**
   * Redo in specific context
   */
  const redoInContext = useCallback(async (contextId) => {
    const result = await historyManager.redoInContext(contextId);
    updateState();
    return result;
  }, [historyManager, updateState]);

  /**
   * Go to specific change
   */
  const goToChange = useCallback(async (changeId) => {
    const result = await historyManager.goToChange(changeId);
    updateState();
    return result;
  }, [historyManager, updateState]);

  /**
   * Create branch
   */
  const createBranch = useCallback((name, description) => {
    const branch = historyManager.createBranch(name, description);
    updateState();
    return branch;
  }, [historyManager, updateState]);

  /**
   * Switch branch
   */
  const switchBranch = useCallback(async (branchId) => {
    const result = await historyManager.switchBranch(branchId);
    updateState();
    return result;
  }, [historyManager, updateState]);

  /**
   * Get history tree
   */
  const getHistoryTree = useCallback(() => {
    return historyManager.getHistoryTree();
  }, [historyManager]);

  /**
   * Get linear history
   */
  const getLinearHistory = useCallback(() => {
    return historyManager.getLinearHistory();
  }, [historyManager]);

  /**
   * Get context history
   */
  const getContextHistory = useCallback((contextId) => {
    return historyManager.getContextHistory(contextId);
  }, [historyManager]);

  /**
   * Get all branches
   */
  const getBranches = useCallback(() => {
    return Array.from(historyManager.branches.values());
  }, [historyManager]);

  /**
   * Export history
   */
  const exportHistory = useCallback(() => {
    return historyManager.export();
  }, [historyManager]);

  /**
   * Import history
   */
  const importHistory = useCallback((data) => {
    historyManager.import(data);
    updateState();
  }, [historyManager, updateState]);

  // Store state and functions in refs to avoid re-registering handler
  const stateRef = useRef(state);
  const undoRef = useRef(undo);
  const redoRef = useRef(redo);
  
  useEffect(() => {
    stateRef.current = state;
    undoRef.current = undo;
    redoRef.current = redo;
  }, [state, undo, redo]);

  // Keyboard shortcuts for undo/redo - register once
  useEffect(() => {
    // console.log('[Backtrackr] Registering keyboard handler'); // Disabled - too noisy
    
    const handleKeyDown = (event) => {
      const { key, metaKey, ctrlKey, shiftKey } = event;
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const cmdOrCtrl = isMac ? metaKey : ctrlKey;
      
      const currentState = stateRef.current;

      // Log ALL Cmd+Z combinations for debugging
      if (cmdOrCtrl && (key === 'z' || key === 'Z')) {
        console.log('[Backtrackr] Z key pressed:', {
          key,
          metaKey,
          ctrlKey,
          shiftKey,
          isMac,
          cmdOrCtrl,
          canUndo: currentState.canUndo,
          canRedo: currentState.canRedo,
          context: currentState.currentContext
        });
      }

      // Cmd+Z or Ctrl+Z - Undo
      if (cmdOrCtrl && (key === 'z' || key === 'Z') && !shiftKey) {
        console.log('[Backtrackr] Undo shortcut pressed. canUndo:', currentState.canUndo, 'context:', currentState.currentContext);
        if (currentState.canUndo) {
          event.preventDefault();
          event.stopPropagation();
          undoRef.current();
        } else {
          console.log('[Backtrackr] Cannot undo - no history available');
        }
        return;
      }

      // Cmd+Shift+Z or Ctrl+Shift+Z - Redo
      if (cmdOrCtrl && (key === 'z' || key === 'Z') && shiftKey) {
        console.log('[Backtrackr] Redo shortcut pressed. canRedo:', currentState.canRedo, 'context:', currentState.currentContext);
        if (currentState.canRedo) {
          event.preventDefault();
          event.stopPropagation();
          redoRef.current();
        } else {
          console.log('[Backtrackr] Cannot redo - no redo history available');
        }
        return;
      }

      // Cmd+Y or Ctrl+Y - Redo (Windows alternative)
      if (cmdOrCtrl && key === 'y' && !isMac) {
        console.log('[Backtrackr] Redo (Y) shortcut pressed. canRedo:', currentState.canRedo);
        if (currentState.canRedo) {
          event.preventDefault();
          event.stopPropagation();
          redoRef.current();
        }
        return;
      }
    };

    // Use capture phase to intercept before other handlers
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, []); // Empty deps - register once!

  const value = useMemo(() => ({
  
    // State
    canUndo: state.canUndo,
    canRedo: state.canRedo,
    currentContext: state.currentContext,
    activeContextHistory: state.activeContextHistory,
    stats: state.stats,
    focusedField: focusedFieldRef.current,

    // Recording changes
    recordChange,
    recordFieldChange,
    recordStructureChange,

    // Focus management
    setFocusedField,
    clearFocusedField,

    // Undo/Redo
    undo,
    redo,
    undoInContext,
    redoInContext,

    // Navigation
    goToChange,

    // Branching
    createBranch,
    switchBranch,
    getBranches,

    // History access
    getHistoryTree,
    getLinearHistory,
    getContextHistory,

    // Import/Export
    exportHistory,
    importHistory,

    // Direct access to manager (for advanced use)
    historyManager
  }), [state.canUndo, state.canRedo, state.currentContext, state.activeContextHistory, state.stats, focusedFieldRef, recordChange, recordFieldChange, recordStructureChange, setFocusedField, clearFocusedField, undo, redo, undoInContext, redoInContext, goToChange, createBranch, switchBranch, getBranches, getHistoryTree, getLinearHistory, getContextHistory, exportHistory, importHistory, historyManager]);

  return (
    <BacktrackrContext.Provider value={value}>
      {children}
    </BacktrackrContext.Provider>
  );
};

/**
 * Hook to use Backtrackr
 */
export const useBacktrackr = (): BacktrackrContextValue => {
  const context = useContext(BacktrackrContext);
  if (!context) {
    throw new Error('useBacktrackr must be used within a BacktrackrProvider');
  }
  return context;
};

/**
 * Hook for field-specific undo/redo
 */
export const useFieldHistory = (nodeId, fieldName) => {
  const backtrackr = useBacktrackr();
  const contextId = `${nodeId}.${fieldName}`;

  const recordChange = useCallback((before, after, metadata) => {
    return backtrackr.recordFieldChange(nodeId, fieldName, before, after, metadata);
  }, [backtrackr, nodeId, fieldName]);

  const undo = useCallback(async () => {
    return backtrackr.undoInContext(contextId);
  }, [backtrackr, contextId]);

  const redo = useCallback(async () => {
    return backtrackr.redoInContext(contextId);
  }, [backtrackr, contextId]);

  const history = backtrackr.getContextHistory(contextId);

  return {
    recordChange,
    undo,
    redo,
    canUndo: history?.canUndo ?? false,
    canRedo: history?.canRedo ?? false,
    history
  };
};

export default BacktrackrContext;
